import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Edit2, Save, X, Camera, Shield, Settings, UserPlus, Eye, EyeOff } from 'lucide-react';
import { adminAPI } from '../services/api';
import { validateImageFile, compressImage } from '../utils/avatarUtils';

export const AdminProfile = ({ user, onUpdateUser }) => {
  const navigate = useNavigate();
  const normalizeGender = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'm' || normalized === 'male') return 'm';
    if (normalized === 'f' || normalized === 'female') return 'f';
    return '';
  };
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editedUser, setEditedUser] = useState({
    fullname: user?.fullname || user?.fullName || '',
    gender: normalizeGender(user?.gender) || 'm',
    avatar: user?.avatar || null
  });
  const fileInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvatarClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setErrorMessage(validation.error);
      return;
    }

    try {
      const compressedDataUrl = await compressImage(file);
      setEditedUser(prev => ({ ...prev, avatar: compressedDataUrl }));
      setErrorMessage('');
    } catch {
      setErrorMessage('Failed to process image. Please try another file.');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Update admin data with all edited fields including avatar
      const updatedUser = {
        ...user,
        fullname: editedUser.fullname,
        fullName: editedUser.fullname, // Keep both for compatibility
        gender: normalizeGender(editedUser.gender) || 'm',
        avatar: editedUser.avatar
      };
      
      // Call the update function passed from parent
      await onUpdateUser(updatedUser);
      
      setIsEditing(false);
      setSuccessMessage('✅ Admin profile updated successfully! Your changes have been saved' + (editedUser.avatar ? ' including your profile picture.' : '.'));
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      
      let errorMsg = 'Failed to update profile. ';
      
      if (error.message?.includes('too large') || error.message?.includes('size')) {
        errorMsg += 'The profile picture is too large. Please try a smaller image.';
      } else if (error.response?.data?.message) {
        errorMsg += error.response.data.message;
      } else {
        errorMsg += 'Please try again or contact support if the issue persists.';
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedUser({
      fullname: user?.fullname || user?.fullName || '',
      gender: normalizeGender(user?.gender) || 'm',
      avatar: user?.avatar || null
    });
    setIsEditing(false);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All password fields are required.');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    if (!user?.id) {
      setPasswordError('Admin ID is missing. Please log in again.');
      return;
    }

    try {
      setIsChangingPassword(true);
      await adminAPI.changePassword(user.id, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setPasswordSuccess('Password changed successfully.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordError(error.message || 'Failed to change password.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = (fullname) => {
    if (!fullname) return 'AD';
    const names = fullname.trim().split(' ');
    if (names.length === 1) return fullname.substring(0, 2).toUpperCase();
    return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
  };

  const getGenderDisplay = (gender) => {
    switch(normalizeGender(gender)) {
      case 'm': return 'Male';
      case 'f': return 'Female';
      default: return 'Not specified';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/home')}
              className="p-2 text-gray-600 hover:text-green-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Admin Profile</h1>
          </div>
          
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  <Save className="w-4 h-4" />
                  <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Success/Error Notifications */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              {/* Avatar Section */}
              <div className="text-center mb-6">
                <button 
                  type="button"
                  className={`relative inline-block ${isEditing ? 'cursor-pointer' : ''}`}
                  onClick={handleAvatarClick}
                  aria-label={isEditing ? 'Change avatar' : 'Admin avatar'}
                  tabIndex={isEditing ? 0 : -1}
                >
                  {(isEditing ? editedUser.avatar : user?.avatar) ? (
                    <img
                      src={isEditing ? editedUser.avatar : user?.avatar}
                      alt="Admin Profile"
                      className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-green-200"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-3xl font-bold mx-auto border-4 border-green-200">
                      {getInitials(editedUser.fullname || user?.fullname || user?.fullName)}
                    </div>
                  )}
                  
                  {isEditing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                  )}
                </button>
                
                {isEditing && (
                  <p className="text-xs text-gray-500 mt-2">Click to upload (Max 5MB, will be resized to 400x400)</p>
                )}
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/*"
                  className="hidden"
                />
                
                <h2 className="text-2xl font-bold text-gray-900 mt-4">
                  {editedUser.fullname || user?.fullname || user?.fullName || 'Administrator'}
                </h2>
                
                <div className="flex items-center justify-center mt-2">
                  <Shield className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-green-700 font-medium">
                    System Administrator
                  </span>
                </div>
                
                {(user?.username || user?.Username) && (
                  <p className="text-gray-600 mt-1">@{user?.username || user?.Username}</p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Gender</span>
                  <span className="font-medium">{getGenderDisplay(user?.gender)}</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">User Role</span>
                  <span className="font-medium">Administrator</span>
                </div>
                
                {user?.id && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600">Admin ID</span>
                    <span className="font-medium text-xs text-gray-500">#{user.id}</span>
                  </div>
                )}
              </div>

              {/* Admin Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => navigate('/admin/applications')}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  <UserPlus className="w-5 h-5" />
                  <span className="font-medium">View Admin Applications</span>
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Review and manage admin access requests
                </p>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-6">
                <Settings className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-xl font-semibold text-gray-900">Profile Information</h3>
              </div>

              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedUser.fullname}
                          onChange={(e) => handleInputChange('fullname', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Enter your full name"
                        />
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span>{user?.fullname || user?.fullName || 'Not provided'}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gender
                      </label>
                      {isEditing ? (
                        <select
                          value={editedUser.gender}
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="m">Male</option>
                          <option value="f">Female</option>
                        </select>
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <span>{getGenderDisplay(user?.gender)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Account Information</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Username
                      </label>
                      <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-500">{user?.username || user?.Username || 'Not available'}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role
                      </label>
                      <div className="flex items-center px-3 py-2 bg-green-50 rounded-lg">
                        <Shield className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-green-700 font-medium">System Administrator</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Full system access and management privileges</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-amber-800 mb-3">Change Password</h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                        className="w-full px-3 pr-10 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                        className="w-full px-3 pr-10 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="New password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                        className="w-full px-3 pr-10 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordError && (
                      <p className="text-sm text-red-600">{passwordError}</p>
                    )}
                    {passwordSuccess && (
                      <p className="text-sm text-green-700">{passwordSuccess}</p>
                    )}
                    <div>
                      <button
                        type="button"
                        onClick={handleChangePassword}
                        disabled={isChangingPassword}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                      >
                        {isChangingPassword ? 'Updating Password...' : 'Update Password'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Admin Privileges */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Administrator Privileges
                  </h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Full access to all farmers and processors data</li>
                    <li>• Manage user accounts and permissions</li>
                    <li>• System configuration and settings</li>
                    <li>• Export and import data capabilities</li>
                    <li>• View analytics and generate reports</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
